import type React from 'react';
import { Link } from 'react-router-dom';
import { BarChart3, Download, ExternalLink } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button, buttonVariants } from '@/components/ui/button';
import { cn } from '@/lib/utils';

/** «Как пользоваться QR-кодом» (Алексей 17.09, AI13.3): только заголовки
 *  шагов, все ссылки — кнопками, главное — что лист вставляется в рамку.
 *  Открывается и до создания формы, чтобы было понятно, зачем она. */

/** Ссылка на поиск типографии рядом — одна на всё приложение. */
export const PRINT_SHOP_SEARCH =
  `https://www.google.com/search?q=${encodeURIComponent('печать наклеек и визиток рядом со мной')}`;

const FRAME_URL = 'https://www.ozon.ru/product/fotoramka-3224469415';

const linkButton = cn(buttonVariants({ variant: 'outline', size: 'sm' }));

export function QrHowto({ open, onOpenChange, onPrint, hasForm }: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Нет формы — нет и кода, кнопку печати не показываем. */
  onPrint?: () => void;
  /** Форма уже создана: первый шаг сделан, и держать его в списке незачем
   *  (Алексей 18.09, AI69) — иначе инструкция вечно начинается с того, что
   *  человек уже нажал. */
  hasForm?: boolean;
}) {
  const steps: { title: string; action?: React.ReactNode }[] = [
    // Порядок — как человек это делает на самом деле (Алексей 18.09, AI57):
    // сначала форма и оформление, и только потом принтер и стол. Раньше первым
    // шагом стояла печать, хотя печатать ещё нечего.
    ...(hasForm ? [] : [{ title: 'Нажмите «Создать форму» ниже' }]),
    { title: 'Выберите оформление' },
    {
      title: 'Скачайте QR-код для печати',
      action: onPrint && (
        <Button size="sm" variant="outline" onClick={() => { onOpenChange(false); onPrint(); }}>
          <Download className="size-3.5" /> Скачать
        </Button>
      ),
    },
    {
      title: 'Распечатайте карточку с кодом на принтере или на специальной карточке в типографии',
      action: (
        <a href={PRINT_SHOP_SEARCH} target="_blank" rel="noreferrer" className={linkButton}>
          <ExternalLink className="size-3.5" /> Типография
        </a>
      ),
    },
    { title: 'Поставьте на кассу и столы' },
    {
      title: 'Читайте отзывы в «Данных»',
      action: (
        <Link to="/management/analytics?tab=reviews" onClick={() => onOpenChange(false)} className={linkButton}>
          <BarChart3 className="size-3.5" /> Открыть
        </Link>
      ),
    },
  ];

  return (
    <Dialog open={open} onOpenChange={onOpenChange} widthClassName="sm:max-w-4xl">
      {/* Окно широкое: в 3xl шаги ужимались в колонку под картинку, а строки
          вроде «распечатайте на принтере или в типографии» ломались на четыре
          строки (Алексей 18.09, AI58). Картинка справа тянется на всю высоту
          шагов — фото квадратное, поэтому подрезка по бокам минимальна. */}
      <DialogContent className="max-h-[calc(100dvh-2rem)] overflow-y-auto sm:max-w-4xl">
        <DialogHeader>
          <DialogTitle>Как пользоваться QR-кодом</DialogTitle>
        </DialogHeader>

        <div className="grid gap-5 sm:grid-cols-[minmax(0,1.6fr)_minmax(15rem,1fr)] sm:items-stretch">
          {/* На телефоне картинка сверху (order-first), на компе — справа. */}
          {/* Рамка — не шаг, а совет сбоку (Алексей 18.09, AI57): в списке шагов
              она сбивала с толку, потому что покупать её необязательно. */}
          <figure className="order-first flex flex-col overflow-hidden rounded-lg border border-border sm:order-last">
            <img
              src="/howto/qr-acrylic-frame.jpg"
              alt="Карточки в акриловых рамках"
              className="max-h-72 min-h-0 w-full flex-1 bg-muted object-cover sm:max-h-none"
            />
            {/* Ссылка в строке (Алексей 18.09, AI58), но ТЕКСТОМ, а не кнопкой:
                высота кнопки раздвигала строки абзаца вокруг себя — «межстрочный
                интервал разрывает» (Алексей 18.09, AI69). */}
            <figcaption className="px-3 py-2 text-sm leading-relaxed">
              <span className="font-medium">Tip:</span> лист из обычного принтера в акриловой рамке
              выглядит как готовая карточка на стол.{' '}
              <a
                href={FRAME_URL}
                target="_blank"
                rel="noreferrer"
                className="whitespace-nowrap font-medium text-primary hover:underline"
              >
                Рамка на Ozon <ExternalLink className="inline size-3.5 align-[-0.125em]" />
              </a>
            </figcaption>
          </figure>

          <ol className="flex flex-col justify-center space-y-3">
            {steps.map((s, i) => (
              <li key={s.title} className="flex flex-wrap items-center gap-x-3 gap-y-2">
                <span className="flex size-6 shrink-0 items-center justify-center rounded-full bg-primary/10 text-xs font-semibold text-primary">
                  {i + 1}
                </span>
                <span className="min-w-0 flex-1 text-sm font-medium">{s.title}</span>
                {s.action && <span className="flex flex-wrap gap-2 pl-9 sm:pl-0">{s.action}</span>}
              </li>
            ))}
          </ol>
        </div>
      </DialogContent>
    </Dialog>
  );
}
